<template>
  <div class="tw-py-0.5">
    <WdsStack direction-row justify-space-between align-center gap-sm>
      <WdsStack direction-column gap-none class="tw-min-w-0">
        <WdsText :text="document.label" secondary sm />
        <WdsText
          v-if="document.expiresAt"
          :text="expiresLabel"
          :tertiary="!isExpired"
          :error="isExpired"
          xs
        />
        <WdsText
          v-if="data.rejectionReason"
          :text="data.rejectionReason"
          tertiary
          xs
          data-test="restricted-document-rejection-reason"
        />
      </WdsStack>

      <WdsStack direction-row align-center gap-xs class="tw-min-w-0 tw-flex-1 tw-justify-end">
        <WdsTag
          sm
          :success="tagVariant === 'success'"
          :error="tagVariant === 'error'"
          :warning="tagVariant === 'warning'"
          data-test="restricted-document-status"
        >
          {{ data.status }}
        </WdsTag>

        <!-- No link when viewUrl is null: the viewer may know the document exists and its review state without opening it. -->
        <button
          v-if="document.viewUrl"
          type="button"
          :title="document.filename"
          class="tw-text-indigo-600 hover:tw-underline tw-text-sm tw-truncate tw-max-w-[160px] tw-bg-transparent tw-border-0 tw-p-0"
          data-test="restricted-document-open-preview"
          @click="previewing = true"
        >
          {{ document.filename }}
        </button>
        <WdsText v-else :text="document.filename" tertiary sm class="tw-truncate tw-max-w-[160px]" />

        <WdsIcon name="document" :size="16" />
      </WdsStack>
    </WdsStack>

    <!--
      The officer decides in the document modal, with the evidence in front of
      them, rather than inline in the compliance review. `canReview` is decided
      server-side per viewer; the mutation re-checks the policy regardless.
    -->
    <RestrictedDocumentPreviewModal
      :show="previewing"
      :view-url="document.viewUrl ?? null"
      :title="document.label"
      @close="previewing = false"
    >
      <template v-if="data.canReview" #footer>
        <RightToWorkDocumentDecision
          :document-id="document.id"
          :approved-at="data.approvedAt ?? null"
          :rejection-reason="data.rejectionReason ?? null"
          @close="previewing = false"
          @decided="previewing = false"
        />
      </template>
    </RestrictedDocumentPreviewModal>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { WdsIcon, WdsStack, WdsTag, WdsText } from '@worksome/wds-components'
import RestrictedDocumentPreviewModal from '@/components/Identification/RestrictedDocumentPreviewModal.vue'
import RightToWorkDocumentDecision from '@/components/compliances/ComplianceReviewModal/RightToWorkDocumentDecision.vue'
import { FragmentType, graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { formatDocumentDate, isDocumentExpired } from '@/utils/restrictedDocument'

// Unlike ComplianceDataFile, the link is a per-request-authorised route, never a signed file.url.
const ComplianceDataRestrictedDocumentFragment = graphql(`
  fragment ComplianceDataRestrictedDocument on ComplianceDataRestrictedDocument {
    document {
      id
      label
      filename
      expiresAt
      viewUrl
    }
    status
    approvedAt
    rejectionReason
    canReview
  }
`)

const props = defineProps<{ entry: FragmentType<typeof ComplianceDataRestrictedDocumentFragment> }>()
const data = computed(() => useFragment(ComplianceDataRestrictedDocumentFragment, props.entry))

const previewing = ref(false)

const document = computed(() => data.value.document)
const isExpired = computed(() => isDocumentExpired(document.value.expiresAt))
const expiresLabel = computed(() =>
  isExpired.value
    ? $t('Expired :date', { date: formatDocumentDate(document.value.expiresAt) })
    : $t('Expires :date', { date: formatDocumentDate(document.value.expiresAt) }),
)

const tagVariant = computed(() => {
  if (data.value.rejectionReason || isExpired.value) return 'error'
  if (data.value.approvedAt) return 'success'

  return 'warning'
})
</script>
